import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiCarthealthcheckPost } from '@r/api/generated/client.ts';
import type {
  CartHealthcheckCartHealthcheckReadModel,
  CartHealthcheckCartHealthcheckWriteModel
} from '@r/api/generated/client.schemas.ts';
import type { ICartItemSchema } from '@r/types/zod-schemas.ts';
import type { z } from 'zod';

type CartItem = z.infer<typeof ICartItemSchema>;

type HealthcheckResult = {
  cartSignature: string;
  response: CartHealthcheckCartHealthcheckReadModel;
};

/** Convertit le panier local dans le format attendu par le healthcheck du back. */
const buildHealthcheckPayload = (
  cartItems: CartItem[]
): CartHealthcheckCartHealthcheckWriteModel => ({
  items: cartItems.map((item) => ({
    product: item.iri,
    quantity: item.quantity
  }))
});

/** Crée une signature qui change dès qu'une ligne ou sa quantité est modifiée. */
const getCartSignature = (cartItems: CartItem[]) =>
  cartItems.map((item) => `${item.iri}:${item.quantity}:${item.addedAt ?? ''}`).join('|');

/**
 * Vérifie le panier auprès du back à son chargement et après chaque modification.
 * Les réponses obsolètes sont ignorées afin qu'un ancien contrôle ne remplace pas le plus récent.
 */
const useCartHealthcheck = (cartItems: CartItem[]) => {
  const itemsRef = useRef(cartItems);
  const requestIdRef = useRef(0);
  const lastRequestedSignatureRef = useRef<string | undefined>(undefined);
  const [result, setResult] = useState<HealthcheckResult>();
  const [isCheckingCart, setIsCheckingCart] = useState(false);
  const [healthcheckFailed, setHealthcheckFailed] = useState(false);
  const cartSignature = useMemo(() => getCartSignature(cartItems), [cartItems]);

  /** Synchronise la référence avec la dernière version validée du panier après le rendu. */
  useEffect(() => {
    itemsRef.current = cartItems;
  }, [cartItems]);

  /** Exécute immédiatement un nouveau contrôle avec la version actuelle du panier. */
  const refreshHealthcheck = useCallback(async () => {
    const items = itemsRef.current;
    const checkedSignature = getCartSignature(items);
    const requestId = ++requestIdRef.current;

    if (items.length === 0) {
      const response: CartHealthcheckCartHealthcheckReadModel = {
        items: [],
        isValid: true
      };
      setResult({ cartSignature: checkedSignature, response });
      setHealthcheckFailed(false);
      return response;
    }

    setIsCheckingCart(true);
    setHealthcheckFailed(false);

    try {
      const response = await apiCarthealthcheckPost(buildHealthcheckPayload(items));

      if (requestId === requestIdRef.current) {
        setResult({ cartSignature: checkedSignature, response });
      }

      return response;
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setResult(undefined);
        setHealthcheckFailed(true);
      }
      throw error;
    } finally {
      if (requestId === requestIdRef.current) {
        setIsCheckingCart(false);
      }
    }
  }, []);

  /** Relance automatiquement le contrôle lorsque le contenu du panier change. */
  useEffect(() => {
    if (lastRequestedSignatureRef.current === cartSignature) return;

    lastRequestedSignatureRef.current = cartSignature;
    void refreshHealthcheck().catch((error) => {
      console.error('Error checking cart health:', error);
    });
  }, [cartSignature, refreshHealthcheck]);

  const currentResponse = result?.cartSignature === cartSignature ? result.response : undefined;

  /** Liste les produits signalés comme invalides par le back pour le panier courant. */
  const invalidProductIris = useMemo(() => {
    const invalidIris = new Set<string>();

    for (const item of currentResponse?.items ?? []) {
      if ((item.isValid === false || item.issue != null) && item.product) {
        invalidIris.add(item.product);
      }
    }

    return invalidIris;
  }, [currentResponse]);
  const isWaitingForFirstCheck =
    cartItems.length > 0 && currentResponse === undefined && !healthcheckFailed;

  return {
    healthcheckFailed,
    invalidProductIris,
    isCartValid: currentResponse?.isValid ?? true,
    isCheckingCart: isCheckingCart || isWaitingForFirstCheck,
    refreshHealthcheck
  };
};

export { buildHealthcheckPayload, useCartHealthcheck };
